import { useEffect, useRef, useState } from 'react';
import { AlertTriangle, FileCheck2, Loader2, Send, X } from 'lucide-react';

import { usePiegeFocus } from '../hooks/usePiegeFocus';
import {
  creerRapport,
  enregistrerRapport,
  lireReferencesRapports,
  type Rapport,
  type RapportVierge,
  type ReferencesRapport,
} from '../api/rapports';

const CLASSE_CHAMP =
  'w-full px-3 py-2.5 bg-white border border-slate-300 rounded-lg text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all';

interface Proprietes {
  readonly ouvert: boolean;
  readonly onFermer: () => void;
  /** Rapport existant : renseigné en lecture et en modification. */
  readonly rapport?: Rapport;
  /** Intervention terminée sans compte rendu : renseignée à la création. */
  readonly vierge?: RapportVierge;
  /** `voir` = lecture seule (le serveur n'autorise plus la modification). */
  readonly mode: 'voir' | 'editer';
  /** Appelé après enregistrement : l'écran parent recharge ses listes. */
  readonly onEnregistre?: (rapport: Rapport) => void;
  /** Passe en édition sans refermer la fenêtre (action « modifier » du serveur). */
  readonly onModifier?: (rapport: Rapport) => void;
}

/**
 * Modale du compte rendu : le même formulaire pour ouvrir, corriger et relire.
 *
 * Aucune règle de statut n'est recopiée ici : les actions autorisées et le
 * message des refus viennent du serveur. Un rapport transmis s'ouvre donc en
 * lecture seule parce que le serveur ne propose plus que « voir ».
 */
export function ModaleRapport({
  ouvert,
  onFermer,
  rapport,
  vierge,
  mode,
  onEnregistre,
  onModifier,
}: Proprietes) {
  const [references, setReferences] = useState<ReferencesRapport>();
  const [constat, setConstat] = useState('');
  const [actionsRealisees, setActionsRealisees] = useState('');
  const [anomalies, setAnomalies] = useState('');
  const [avis, setAvis] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState<string>();
  const premierChamp = useRef<HTMLTextAreaElement>(null);
  const boite = useRef<HTMLDivElement>(null);
  usePiegeFocus(boite, ouvert);

  const lecture = mode === 'voir';
  const titre = lecture
    ? 'Compte rendu d’intervention'
    : rapport
      ? 'Modifier le compte rendu'
      : 'Remplir le compte rendu';

  // Chaque ouverture repart de la donnée : un état laissé d'une modale à l'autre
  // ferait écrire le constat d'un chantier sur un autre.
  useEffect(() => {
    if (!ouvert) return;
    setErreur(undefined);
    setEnvoi(false);
    setConstat(rapport?.constat ?? '');
    setActionsRealisees(rapport?.actionsRealisees ?? '');
    setAnomalies(rapport?.anomalies ?? '');
    setAvis(rapport?.avis ?? '');
  }, [ouvert, rapport]);

  useEffect(() => {
    if (!ouvert || references) return;
    const controleur = new AbortController();
    void lireReferencesRapports({ signal: controleur.signal })
      .then((resultat) => {
        if (resultat.ok && resultat.donnees) setReferences(resultat.donnees);
        else setErreur(resultat.message ?? 'Listes du formulaire indisponibles.');
      })
      .catch(() => {
        /* abandon : modale fermée */
      });
    return () => controleur.abort();
  }, [ouvert, references]);

  useEffect(() => {
    if (ouvert && !lecture) premierChamp.current?.focus();
  }, [ouvert, lecture]);

  useEffect(() => {
    if (!ouvert) return;
    const surTouche = (evenement: KeyboardEvent) => {
      if (evenement.key === 'Escape') onFermer();
    };
    document.addEventListener('keydown', surTouche);
    return () => document.removeEventListener('keydown', surTouche);
  }, [ouvert, onFermer]);

  const enregistrer = async (soumettre: boolean) => {
    setEnvoi(true);
    setErreur(undefined);
    const saisie = {
      constat,
      actionsRealisees,
      anomalies,
      avis,
      ...(rapport ? {} : { interventionId: vierge?.interventionId ?? '' }),
      soumettre,
    };
    const resultat = rapport
      ? await enregistrerRapport(rapport.id, saisie)
      : await creerRapport(saisie);
    setEnvoi(false);
    if (resultat.ok && resultat.donnees) {
      onEnregistre?.(resultat.donnees);
      onFermer();
      return;
    }
    // Le message du serveur est préféré au nôtre : il nomme le champ à corriger.
    setErreur(resultat.message ?? 'Enregistrement impossible.');
  };

  if (!ouvert) return null;

  const contexte = rapport ?? vierge;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50"
      onClick={(evenement) => {
        // Un clic sur le voile ferme : c'est le geste attendu d'une fenêtre.
        if (evenement.target === evenement.currentTarget) onFermer();
      }}
    >
      <div
        ref={boite}
        role="dialog"
        aria-modal="true"
        aria-labelledby="titre-modale-rapport"
        className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col"
      >
        <div className="flex items-start justify-between gap-4 px-6 py-4 border-b border-slate-200">
          <div className="flex items-start gap-3">
            <span className="w-9 h-9 rounded-full bg-red-50 text-red-600 flex items-center justify-center flex-shrink-0">
              <FileCheck2 className="w-4 h-4" aria-hidden="true" />
            </span>
            <div>
              <h2 id="titre-modale-rapport" className="text-base font-semibold text-slate-900">
                {titre}
              </h2>
              <p className="text-xs text-slate-500 mt-0.5">
                {contexte?.client} · {contexte?.dateLisible ?? ''}
                {rapport ? ` · ${rapport.typeRapport}` : ` · ${vierge?.typeIntervention ?? ''}`}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onFermer}
            className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-md"
            aria-label="Fermer la fenêtre"
          >
            <X className="w-4 h-4" aria-hidden="true" />
          </button>
        </div>

        <div className="px-6 py-5 overflow-y-auto space-y-4">
          {erreur && (
            <div role="alert" className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-800">{erreur}</p>
            </div>
          )}

          {rapport?.motifRejet && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
              <AlertTriangle
                className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5"
                aria-hidden="true"
              />
              <div>
                <p className="text-sm font-medium text-amber-800">Motif du rejet</p>
                <p className="text-sm text-amber-900">{rapport.motifRejet}</p>
              </div>
            </div>
          )}

          {lecture ? (
            <div className="space-y-3 text-sm">
              <Ligne libelle="Lieu" valeur={contexte?.lieu ?? ''} />
              <Ligne libelle="Constat" valeur={rapport?.constat ?? ''} />
              <Ligne libelle="Actions réalisées" valeur={rapport?.actionsRealisees ?? '—'} />
              <Ligne
                libelle="Anomalies"
                valeur={rapport?.anomalies ?? 'Aucune anomalie signalée'}
              />
              <Ligne libelle="Avis" valeur={rapport?.avisLibelle ?? 'Non renseigné'} />
              <Ligne libelle="Dernière modification" valeur={rapport?.modifieLisible ?? ''} />
              {rapport?.controleLisible && (
                <Ligne
                  libelle="Contrôle"
                  valeur={`${rapport.controleLisible}${rapport.controlePar ? ` par ${rapport.controlePar}` : ''}`}
                />
              )}
            </div>
          ) : (
            <>
              <div>
                <label
                  htmlFor="rapport-constat"
                  className="block text-sm font-medium text-slate-700 mb-1.5"
                >
                  Constat <span className="text-red-600">*</span>
                </label>
                <textarea
                  id="rapport-constat"
                  ref={premierChamp}
                  value={constat}
                  onChange={(evenement) => setConstat(evenement.target.value)}
                  rows={4}
                  aria-describedby="aide-constat"
                  className={CLASSE_CHAMP}
                  placeholder="Ce qui a été constaté sur site : équipements contrôlés, essais, mesures…"
                />
                <div className="flex justify-between items-start gap-3 mt-1">
                  <p id="aide-constat" className="text-xs text-slate-500">
                    Au moins {references?.constatMin ?? 10} caractères — un compte rendu vide ne
                    remplace pas une intervention.
                  </p>
                  {/* Le compteur suit la règle du SERVEUR : aucune borne recopiée ici. */}
                  <p className="text-xs text-slate-500 shrink-0" aria-live="polite">
                    {constat.trim().length}/{references?.texteMax ?? 2000}
                  </p>
                </div>
              </div>

              <div>
                <label
                  htmlFor="rapport-actions"
                  className="block text-sm font-medium text-slate-700 mb-1.5"
                >
                  Actions réalisées
                </label>
                <textarea
                  id="rapport-actions"
                  value={actionsRealisees}
                  onChange={(evenement) => setActionsRealisees(evenement.target.value)}
                  rows={2}
                  className={CLASSE_CHAMP}
                  placeholder="Remplacements, réglages, essais fonctionnels…"
                />
              </div>

              <div>
                <label
                  htmlFor="rapport-anomalies"
                  className="block text-sm font-medium text-slate-700 mb-1.5"
                >
                  Anomalies relevées
                </label>
                <textarea
                  id="rapport-anomalies"
                  value={anomalies}
                  onChange={(evenement) => setAnomalies(evenement.target.value)}
                  rows={2}
                  className={CLASSE_CHAMP}
                  placeholder="À laisser vide si rien n’a été relevé."
                />
              </div>

              <div>
                <label
                  htmlFor="rapport-avis"
                  className="block text-sm font-medium text-slate-700 mb-1.5"
                >
                  Avis sur l’installation
                </label>
                <select
                  id="rapport-avis"
                  value={avis}
                  onChange={(evenement) => setAvis(evenement.target.value)}
                  className={CLASSE_CHAMP}
                >
                  <option value="">Non renseigné</option>
                  {(references?.avis ?? []).map((entree) => (
                    <option key={entree.code} value={entree.code}>
                      {entree.libelle}
                    </option>
                  ))}
                </select>
              </div>
            </>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200">
          <button
            type="button"
            onClick={onFermer}
            className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50"
          >
            {lecture ? 'Fermer' : 'Annuler'}
          </button>

          {lecture && rapport && onModifier && (
            <button
              type="button"
              onClick={() => onModifier(rapport)}
              className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50"
            >
              {rapport.actions.find((action) => action.code === 'modifier')?.libelle ??
                'Modifier le compte rendu'}
            </button>
          )}

          {!lecture && (
            <>
              <button
                type="button"
                onClick={() => void enregistrer(false)}
                disabled={envoi}
                className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-60 flex items-center gap-2"
              >
                {envoi && <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />}
                Enregistrer le brouillon
              </button>
              <button
                type="button"
                onClick={() => void enregistrer(true)}
                disabled={envoi}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-60 flex items-center gap-2"
              >
                {!envoi && <Send className="w-4 h-4" aria-hidden="true" />}
                Transmettre au contrôle
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Ligne({ libelle, valeur }: { readonly libelle: string; readonly valeur: string }) {
  return (
    <div>
      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{libelle}</p>
      <p className="text-sm text-slate-800 whitespace-pre-line">{valeur}</p>
    </div>
  );
}
